import type { ToolCall, RichContent } from '../types';

export interface SSETextDelta {
  type: 'text_delta';
  text: string;
}

export interface SSEToolCall {
  type: 'tool_call';
  toolCall: ToolCall;
}

export interface SSEToolResult {
  type: 'tool_result';
  id: string;
  result: unknown;
}

export interface SSECitation {
  type: 'citation';
  source: { id: string; title: string; url: string; snippet?: string };
}

export interface SSEThinking {
  type: 'thinking';
  text: string;
}

export interface SSERichContent {
  type: 'rich_content';
  messageType: string;
  content: RichContent;
}

export interface SSEOutcome {
  type: 'outcome';
  outcome: unknown;
}

export interface SSEEscalated {
  type: 'escalated';
  threadId?: string;
}

export type SSEEvent =
  | SSETextDelta
  | SSEToolCall
  | SSEToolResult
  | SSECitation
  | SSEThinking
  | SSERichContent
  | SSEOutcome
  | SSEEscalated;

/**
 * Parse a single SSE data line (after stripping "data: " prefix) into a typed event.
 * Returns null if the line cannot be parsed or the event type is unrecognized.
 */
export function parseSSEData(
  json: string,
  options: {
    enableToolVisualization: boolean;
    enableSourceCitations: boolean;
    enableThinking: boolean;
    enableRichMessages?: boolean;
  }
): SSEEvent | null {
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }

  if (data.type === 'text_delta' || (data.delta as Record<string, unknown>)?.text) {
    const delta = data.delta as Record<string, unknown> | undefined;
    return {
      type: 'text_delta',
      text: (delta?.text ?? data.text ?? '') as string,
    };
  }

  if (data.type === 'tool_call' && options.enableToolVisualization) {
    const id = typeof data.id === 'string' ? data.id : `tool-${Date.now()}`;
    const name =
      typeof data.name === 'string'
        ? data.name
        : typeof data.tool === 'string'
          ? data.tool
          : 'unknown';
    return {
      type: 'tool_call',
      toolCall: {
        id,
        name,
        arguments: (data.arguments || data.args) as Record<string, unknown> | undefined,
        status: 'running',
      },
    };
  }

  if (data.type === 'tool_result' && options.enableToolVisualization) {
    if (typeof data.id !== 'string') return null;
    return {
      type: 'tool_result',
      id: data.id,
      result: data.result,
    };
  }

  if (data.type === 'citation' && options.enableSourceCitations) {
    const title = typeof data.title === 'string' ? data.title : '';
    const url = typeof data.url === 'string' ? data.url : '';
    return {
      type: 'citation',
      source: {
        id: typeof data.id === 'string' ? data.id : `source-${Date.now()}`,
        title,
        url,
        snippet: typeof data.snippet === 'string' ? data.snippet : undefined,
      },
    };
  }

  if (data.type === 'thinking' && options.enableThinking) {
    return {
      type: 'thinking',
      text: (data.text ?? '') as string,
    };
  }

  if (
    (data.type === 'product_cards' ||
      data.type === 'order_cards' ||
      data.type === 'card' ||
      data.type === 'buttons' ||
      data.type === 'form') &&
    options.enableRichMessages !== false
  ) {
    const content = (data.content || data.richContent || data.payload) as RichContent | undefined;
    if (!content) return null;
    return {
      type: 'rich_content',
      messageType: data.type as string,
      content,
    };
  }

  if (data.type === 'outcome' || data.type === 'agentic_outcome') {
    const payload =
      (data.outcomes as unknown[] | undefined) ??
      (data.outcome as unknown) ??
      (data.payload as unknown) ??
      (() => {
        const { type: _type, ...rest } = data;
        return rest;
      })();
    return { type: 'outcome', outcome: payload };
  }

  if (data.type === 'escalated') {
    return { type: 'escalated', threadId: data.threadId as string | undefined };
  }

  return null;
}

/**
 * Parse a chunk of SSE text (potentially multiple lines) and yield events.
 */
export function parseSSEChunk(
  chunk: string,
  options: {
    enableToolVisualization: boolean;
    enableSourceCitations: boolean;
    enableThinking: boolean;
    enableRichMessages?: boolean;
  }
): SSEEvent[] {
  const events: SSEEvent[] = [];
  const lines = chunk.split('\n');

  for (const line of lines) {
    if (line.startsWith('data:')) {
      let payload = line.slice(5);
      if (payload.startsWith(' ')) payload = payload.slice(1);
      const event = parseSSEData(payload, options);
      if (event) events.push(event);
    }
  }

  return events;
}
